import React, { useState } from 'react';
import { Search, Zap, BarChart3, Radar, Users, Trophy } from 'lucide-react';
import { BRAND_GREEN, BRAND_GOLD } from '../../theme/sucronTheme';


/**
 * VisualQueryBuilder - Interface "Mad Libs" para construção de insights
 * 
 * Templates disponíveis:
 * 1. 'player-comparison' → Compare até 5 jogadores em radar
 * 2. 'league-comparison' → Compare até 10 ligas em barras
 * 3. 'team-style' → Analise o estilo de até 6 times
 * 4. 'match-highlight' → Destaque de estatística de partida
 * 
 * @param {Function} onBuild - Callback quando usuário constrói insight
 *   Recebe objeto: { template, params, title, subtitle }
 */
export default function VisualQueryBuilder({ onBuild }) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  const templates = [
    {
      id: 'player-comparison',
      icon: Radar,
      title: 'Comparar Jogadores',
      description: 'Radar multidimensional de até 5 jogadores',
      color: BRAND_GREEN,
      fields: [
        {
          name: 'playerIds',
          label: 'IDs dos Jogadores (separados por vírgula)',
          type: 'text',
          placeholder: '123,456,789',
          required: true,
          hint: 'Mínimo 2, máximo 5 jogadores'
        },
        {
          name: 'season',
          label: 'Temporada',
          type: 'text',
          placeholder: '2024/25',
          required: true
        },
        {
          name: 'dimensions',
          label: 'Dimensões (opcional)',
          type: 'text',
          placeholder: 'goals,assists,passing,duels,tackles,creativity',
          required: false,
          hint: 'Padrão: todas as 6 dimensões'
        }
      ]
    },
    {
      id: 'league-comparison',
      icon: BarChart3,
      title: 'Comparar Ligas',
      description: 'Gráfico de barras comparando estatísticas entre ligas',
      color: '#374151',
      fields: [
        {
          name: 'leagueIds',
          label: 'IDs das Ligas (separados por vírgula)',
          type: 'text',
          placeholder: '39,140,135,78',
          required: true,
          hint: 'Máximo 10 ligas'
        },
        {
          name: 'metric',
          label: 'Métrica',
          type: 'select',
          required: true,
          options: [
            { value: 'goals_per_game', label: 'Gols por Jogo' },
            { value: 'shots_per_game', label: 'Chutes por Jogo' },
            { value: 'pass_accuracy', label: 'Precisão de Passe (%)' },
            { value: 'possession_avg', label: 'Posse Média (%)' },
            { value: 'cards_per_game', label: 'Cartões por Jogo' }
          ]
        },
        {
          name: 'season',
          label: 'Temporada',
          type: 'text',
          placeholder: '2024/25',
          required: true
        }
      ]
    },
    {
      id: 'team-style',
      icon: Users,
      title: 'Estilo de Times',
      description: 'Análise comparativa do estilo de jogo',
      color: '#0369a1',
      fields: [
        {
          name: 'teamIds',
          label: 'IDs dos Times (separados por vírgula)',
          type: 'text',
          placeholder: '50,33,40',
          required: true,
          hint: 'Mínimo 2, máximo 6 times'
        },
        {
          name: 'season',
          label: 'Temporada',
          type: 'text',
          placeholder: '2024/25',
          required: true
        }
      ]
    },
    {
      id: 'match-highlight',
      icon: Trophy,
      title: 'Destaque de Partida',
      description: 'Estatística destacada de uma partida específica',
      color: BRAND_GOLD,
      fields: [
        {
          name: 'matchId',
          label: 'ID da Partida',
          type: 'text',
          placeholder: '12345',
          required: true
        },
        {
          name: 'metric',
          label: 'Métrica',
          type: 'select',
          required: true,
          options: [
            { value: 'goals', label: 'Gols' },
            { value: 'shots', label: 'Chutes' },
            { value: 'possession', label: 'Posse de Bola' },
            { value: 'passes', label: 'Passes' },
            { value: 'fouls', label: 'Faltas' }
          ]
        }
      ]
    }
  ];

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFormData({});
    setErrors({});
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    // Limpar erro ao digitar
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const template = selectedTemplate;

    template.fields.forEach(field => {
      if (field.required && !formData[field.name]) {
        newErrors[field.name] = 'Campo obrigatório';
      }

      // Validações específicas
      if (field.name === 'playerIds' && formData[field.name]) {
        const ids = formData[field.name].split(',').map(id => id.trim()).filter(Boolean);
        if (ids.length < 2) {
          newErrors[field.name] = 'Mínimo 2 jogadores';
        } else if (ids.length > 5) {
          newErrors[field.name] = 'Máximo 5 jogadores';
        }
      }

      if (field.name === 'teamIds' && formData[field.name]) {
        const ids = formData[field.name].split(',').map(id => id.trim()).filter(Boolean);
        if (ids.length < 2) {
          newErrors[field.name] = 'Mínimo 2 times';
        } else if (ids.length > 6) {
          newErrors[field.name] = 'Máximo 6 times';
        }
      }

      if (field.name === 'leagueIds' && formData[field.name]) {
        const ids = formData[field.name].split(',').map(id => id.trim()).filter(Boolean);
        if (ids.length > 10) {
          newErrors[field.name] = 'Máximo 10 ligas';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBuild = () => {
    if (!validateForm()) {
      return;
    }

    // Gerar título e subtítulo automáticos
    let title = '';
    let subtitle = '';

    switch (selectedTemplate.id) {
      case 'player-comparison':
        const playerCount = formData.playerIds.split(',').length;
        title = `Comparação de ${playerCount} Jogadores`;
        subtitle = `Temporada ${formData.season || '2024/25'}`;
        break;
      case 'league-comparison':
        const leagueCount = formData.leagueIds.split(',').length;
        const metricLabel = selectedTemplate.fields[1].options.find(
          opt => opt.value === formData.metric
        )?.label || 'Métrica';
        title = `${metricLabel} - ${leagueCount} Ligas`;
        subtitle = `Temporada ${formData.season || '2024/25'}`;
        break;
      case 'team-style':
        const teamCount = formData.teamIds.split(',').length;
        title = `Estilo de Jogo - ${teamCount} Times`;
        subtitle = `Temporada ${formData.season || '2024/25'}`;
        break;
      case 'match-highlight':
        const highlightMetric = selectedTemplate.fields[1].options.find(
          opt => opt.value === formData.metric
        )?.label || 'Estatística';
        title = `Destaque: ${highlightMetric}`;
        subtitle = `Partida #${formData.matchId}`;
        break;
      default:
        title = 'Insight Editorial';
    }

    onBuild({
      template: selectedTemplate.id,
      params: formData,
      title,
      subtitle
    });
  };

  const renderTemplateGrid = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {templates.map(template => {
        const Icon = template.icon;
        return (
          <button
            key={template.id}
            onClick={() => handleTemplateSelect(template)}
            className="group relative bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-green-600 transition-all shadow-sm hover:shadow-lg text-left"
          >
            <div className="flex items-start gap-4">
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center text-white"
                style={{ backgroundColor: template.color }}
              >
                <Icon size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800 mb-1">
                  {template.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {template.description}
                </p>
              </div>
            </div>
            <div className="absolute top-6 right-6">
              <Zap 
                size={20} 
                className="text-gray-400 group-hover:text-yellow-500 transition"
              />
            </div>
          </button>
        );
      })}
    </div>
  );

  const renderForm = () => {
    const Icon = selectedTemplate.icon;
    return (
      <div className="max-w-2xl mx-auto">
        {/* Header do Template */}
        <div 
          className="rounded-xl p-6 mb-6 text-white"
          style={{ background: `linear-gradient(135deg, ${selectedTemplate.color} 0%, ${BRAND_GREEN} 100%)` }}
        >
          <button
            onClick={() => setSelectedTemplate(null)}
            className="text-white hover:text-gray-200 mb-4 text-sm font-medium"
          >
            ← Voltar aos templates
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
              <Icon size={32} />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-1">
                {selectedTemplate.title}
              </h2>
              <p className="text-sm text-white/90">
                {selectedTemplate.description}
              </p>
            </div>
          </div>
        </div>

        {/* Formulário */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="space-y-5">
            {selectedTemplate.fields.map(field => (
              <div key={field.name}>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                
                {field.type === 'text' && (
                  <input
                    type="text"
                    placeholder={field.placeholder}
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition ${
                      errors[field.name] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                )}

                {field.type === 'select' && (
                  <select
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition ${
                      errors[field.name] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Selecione...</option>
                    {field.options.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}

                {field.hint && (
                  <p className="text-xs text-gray-500 mt-1">
                    {field.hint}
                  </p>
                )}

                {errors[field.name] && (
                  <p className="text-xs text-red-500 mt-1 font-medium">
                    {errors[field.name]}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Botão de Construir */}
          <button
            onClick={handleBuild}
            className="w-full mt-8 py-4 rounded-lg text-white font-bold text-lg hover:opacity-90 transition shadow-lg"
            style={{ background: `linear-gradient(135deg, ${BRAND_GREEN} 0%, #374151 100%)` }}
          >
            <div className="flex items-center justify-center gap-2">
              <Zap size={20} />
              <span>Construir Insight</span>
            </div>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="py-8">
      {!selectedTemplate ? (
        <>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              Escolha um Template
            </h2>
            <p className="text-gray-600">
              Selecione um tipo de análise para começar
            </p>
          </div>
          {renderTemplateGrid()}
        </>
      ) : (
        renderForm()
      )}
    </div>
  );
}
